import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { 
  getUserOwnedOrMemberProjects, 
  isProjectOwnerOrMember,
  calculateProjectSkillMatch 
} from '../utils/userPermissions';
import { 
  Plus, 
  Search, 
  Users, 
  Target,
  Calendar,
  FolderGit2,
  Trash2,
  Sparkles,
  Send,
  CheckCircle,
  CheckCircle2,
  Clock,
  X,
  Compass,
  Briefcase,
  AlertCircle,
  Edit3
} from 'lucide-react';
import { api } from '../services/api';

const Projects = () => {
  const { isDarkMode } = useTheme();
  const { currentUser, isAdmin } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedSkillFilter, setSelectedSkillFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'open', 'fit', 'my', 'applied'

  const [projects, setProjects] = useState(() => {
    const savedProjects = localStorage.getItem('skillmesh_projects');
    return savedProjects !== null ? JSON.parse(savedProjects) : [];
  });

  const [applications, setApplications] = useState(() => {
    const savedApps = localStorage.getItem('skillmesh_applications');
    return savedApps !== null ? JSON.parse(savedApps) : [];
  });

  // Application Modal state
  const [applyModalProject, setApplyModalProject] = useState(null);
  const [applicationPitch, setApplicationPitch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState(null);

  // Sync projects to localStorage
  useEffect(() => {
    localStorage.setItem('skillmesh_projects', JSON.stringify(projects));
  }, [projects]);

  // Load live data from API and sync with storage events
  const refreshData = async () => {
    try {
      const [projData, appsData] = await Promise.all([
        api.getProjects(),
        api.getApplications()
      ]);
      if (Array.isArray(projData)) setProjects(projData);
      if (Array.isArray(appsData)) setApplications(appsData);
    } catch (e) {
      console.warn('Error refreshing projects & applications:', e);
    }
  };

  useEffect(() => {
    refreshData();

    const handleStorageChange = () => {
      const savedProjects = localStorage.getItem('skillmesh_projects');
      if (savedProjects) setProjects(JSON.parse(savedProjects));
      const savedApps = localStorage.getItem('skillmesh_applications');
      if (savedApps) setApplications(JSON.parse(savedApps));
    };

    window.addEventListener('storage', handleStorageChange);
    const handleVisibilityChange = () => {
      if (!document.hidden) handleStorageChange();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleDeleteProject = async (projectId, projectName) => {
    if (window.confirm(`Are you sure you want to permanently delete project "${projectName}" from Neo4j?`)) {
      await api.deleteProject(projectId);
      setProjects(prev => prev.filter(p => String(p.id) !== String(projectId) && p.name !== projectId));
    }
  };

  // Helper to check user application status for a project
  const getUserApplication = (projectId) => {
    if (!currentUser?.email) return null;
    const userEmail = currentUser.email.toLowerCase();
    return applications.find(a => 
      (String(a.projectId) === String(projectId) || a.projectName === projectId) && 
      (a.userEmail || '').toLowerCase() === userEmail
    );
  };

  // Extract all unique skills across all projects for filtering
  const allProjectSkills = Array.from(
    new Set(
      projects.flatMap(p => {
        if (!Array.isArray(p.requiredSkills)) return [];
        return p.requiredSkills.map(s => typeof s === 'string' ? s : s?.name).filter(Boolean);
      })
    )
  ).sort();

  // Handle submitting an application
  const handleOpenApplyModal = (project) => {
    setApplyModalProject(project);
    setApplicationPitch(`Hello! I'm interested in joining the "${project.name}" team. My skills align well with the project requirements, and I would love to contribute.`);
  };

  const handleCloseApplyModal = () => {
    setApplyModalProject(null);
    setApplicationPitch('');
    setIsSubmitting(false);
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    if (!applyModalProject || !currentUser) return;

    setIsSubmitting(true);
    try {
      const userSkillNames = (currentUser.skills || []).map(s => typeof s === 'string' ? s : s.name);
      const appRecord = await api.applyToProject({
        projectId: applyModalProject.id,
        projectName: applyModalProject.name,
        userEmail: currentUser.email,
        userName: currentUser.name || currentUser.email.split('@')[0],
        message: applicationPitch,
        skills: userSkillNames
      });

      setApplications(prev => [appRecord, ...prev.filter(a => a.id !== appRecord.id)]);
      setFeedbackMessage({
        type: 'success',
        text: `Application submitted successfully for "${applyModalProject.name}"!`
      });
      setTimeout(() => setFeedbackMessage(null), 5000);
      handleCloseApplyModal();
    } catch (err) {
      setFeedbackMessage({
        type: 'error',
        text: 'Failed to submit application. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter projects by activeTab, search, skill, and status
  const userOwnedProjects = getUserOwnedOrMemberProjects(projects, currentUser, isAdmin);

  const filteredProjects = projects.filter(project => {
    // 1. Text search
    const matchesSearch = 
      (project.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.requiredSkills || []).some(s => {
        const name = typeof s === 'string' ? s : s?.name || '';
        return name.toLowerCase().includes(searchTerm.toLowerCase());
      });

    // 2. Status filter
    const matchesStatus = filterStatus === 'all' || project.status === filterStatus;

    // 3. Skill filter
    const matchesSkill = selectedSkillFilter === 'all' || (project.requiredSkills || []).some(s => {
      const name = typeof s === 'string' ? s : s?.name || '';
      return name.toLowerCase() === selectedSkillFilter.toLowerCase();
    });

    if (!matchesSearch || !matchesStatus || !matchesSkill) return false;

    // 4. Tab filtering
    const isOwnerOrMember = isProjectOwnerOrMember(project, currentUser);
    const existingApp = getUserApplication(project.id);
    const matchInfo = calculateProjectSkillMatch(project.requiredSkills, currentUser?.skills);
    const teamFull = (project.currentTeam || 0) >= (project.teamSize || 3);

    if (activeTab === 'all') {
      return true;
    }
    if (activeTab === 'open') {
      // Open for applications: not already on team, not full, and not already applied
      return !isOwnerOrMember && !teamFull && !existingApp;
    }
    if (activeTab === 'fit') {
      // High match with user skills
      return matchInfo.matchPercentage >= 50;
    }
    if (activeTab === 'my') {
      // User is creator or member
      return isOwnerOrMember;
    }
    if (activeTab === 'applied') {
      // User has submitted an application
      return !!existingApp;
    }

    return true;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return isDarkMode
          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
          : 'bg-green-50 text-green-800 border border-green-300 font-semibold';
      case 'In Progress':
        return isDarkMode
          ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
          : 'bg-blue-50 text-blue-800 border border-blue-300 font-semibold';
      case 'Planning':
        return isDarkMode
          ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
          : 'bg-amber-100 text-amber-950 border border-amber-300 font-semibold';
      case 'On Hold':
        return isDarkMode
          ? 'bg-red-500/20 text-red-400 border border-red-500/30'
          : 'bg-red-50 text-red-800 border border-red-300 font-semibold';
      default:
        return isDarkMode ? 'bg-dark-700 text-dark-300' : 'bg-light-200 text-light-700';
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-green-500';
    if (progress >= 60) return 'bg-blue-500';
    if (progress >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const appliedCount = applications.filter(a => (a.userEmail || '').toLowerCase() === (currentUser?.email || '').toLowerCase()).length;

  return (
    <div className={`space-y-6 transition-colors duration-200 ${
      isDarkMode ? 'text-white' : 'text-light-950'
    }`}>
      {/* Toast Alert */}
      {feedbackMessage && (
        <div className={`p-4 rounded-xl flex items-center justify-between border ${
          feedbackMessage.type === 'success' 
            ? 'bg-green-500/10 border-green-500/30 text-green-400' 
            : 'bg-red-500/10 border-red-500/30 text-red-400'
        }`}>
          <div className="flex items-center space-x-3">
            {feedbackMessage.type === 'success' ? <CheckCircle2 className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
            <span className="text-sm font-medium">{feedbackMessage.text}</span>
          </div>
          <button onClick={() => setFeedbackMessage(null)} className="text-sm opacity-70 hover:opacity-100">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center flex-wrap gap-2">
            <h1 className="text-3xl font-bold">Projects & Opportunities</h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary-500/20 text-primary-400 border border-primary-500/30">
              {projects.length} Total Projects
            </span>
            {appliedCount > 0 && (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                {appliedCount} Applied
              </span>
            )}
          </div>
          <p className={`mt-1 text-sm transition-colors duration-200 ${
            isDarkMode ? 'text-dark-300' : 'text-light-600'
          }`}>
            Browse all projects across SkillMesh, inspect required skills, evaluate your skill fit, and apply to join teams.
          </p>
        </div>
        
        <div className="flex space-x-3">
          <Link
            to="/me"
            className="btn-secondary flex items-center text-sm"
            title="View or update your skills for better project matching"
          >
            <Target className="h-4 w-4 mr-2" />
            My Skills Profile
          </Link>
          <Link
            to="/projects/new"
            className="btn-primary flex items-center text-sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Project
          </Link>
        </div>
      </div>

      {/* Navigation Filter Tabs */}
      <div className="flex items-center space-x-2 overflow-x-auto pb-1 border-b border-dark-800">
        <button
          onClick={() => setActiveTab('all')}
          className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'all'
              ? 'bg-gradient-primary text-white shadow-md'
              : isDarkMode ? 'text-dark-300 hover:text-white hover:bg-dark-800' : 'text-light-600 hover:text-light-900 hover:bg-light-100'
          }`}
        >
          <Compass className="h-4 w-4" />
          <span>All Projects</span>
          <span className="text-xs bg-black/20 px-1.5 py-0.5 rounded-full">{projects.length}</span>
        </button>

        <button
          onClick={() => setActiveTab('open')}
          className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'open'
              ? 'bg-gradient-primary text-white shadow-md'
              : isDarkMode ? 'text-dark-300 hover:text-white hover:bg-dark-800' : 'text-light-600 hover:text-light-900 hover:bg-light-100'
          }`}
        >
          <Sparkles className="h-4 w-4 text-amber-400" />
          <span>Open to Apply</span>
          <span className="text-xs bg-black/20 px-1.5 py-0.5 rounded-full">
            {projects.filter(p => !isProjectOwnerOrMember(p, currentUser) && (p.currentTeam || 0) < (p.teamSize || 3) && !getUserApplication(p.id)).length}
          </span>
        </button>

        <button
          onClick={() => setActiveTab('fit')}
          className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'fit'
              ? 'bg-gradient-primary text-white shadow-md'
              : isDarkMode ? 'text-dark-300 hover:text-white hover:bg-dark-800' : 'text-light-600 hover:text-light-900 hover:bg-light-100'
          }`}
        >
          <Target className="h-4 w-4 text-green-400" />
          <span>Best Skill Fit</span>
          <span className="text-xs bg-black/20 px-1.5 py-0.5 rounded-full">
            {projects.filter(p => calculateProjectSkillMatch(p.requiredSkills, currentUser?.skills).matchPercentage >= 50).length}
          </span>
        </button>

        <button
          onClick={() => setActiveTab('my')}
          className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'my'
              ? 'bg-gradient-primary text-white shadow-md'
              : isDarkMode ? 'text-dark-300 hover:text-white hover:bg-dark-800' : 'text-light-600 hover:text-light-900 hover:bg-light-100'
          }`}
        >
          <Briefcase className="h-4 w-4 text-blue-400" />
          <span>My Projects</span>
          <span className="text-xs bg-black/20 px-1.5 py-0.5 rounded-full">{userOwnedProjects.length}</span>
        </button>

        <button
          onClick={() => setActiveTab('applied')}
          className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'applied'
              ? 'bg-gradient-primary text-white shadow-md'
              : isDarkMode ? 'text-dark-300 hover:text-white hover:bg-dark-800' : 'text-light-600 hover:text-light-900 hover:bg-light-100'
          }`}
        >
          <Send className="h-4 w-4 text-purple-400" />
          <span>My Applications</span>
          <span className="text-xs bg-black/20 px-1.5 py-0.5 rounded-full">{appliedCount}</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="relative md:col-span-2">
          <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${
            isDarkMode ? 'text-dark-400' : 'text-light-500'
          }`} />
          <input
            type="text"
            placeholder="Search projects by name, description, or skill..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`input-field w-full pl-10 transition-all duration-200 ${
              isDarkMode ? 'dark' : 'light'
            }`}
          />
        </div>
        
        <div>
          <select
            value={selectedSkillFilter}
            onChange={(e) => setSelectedSkillFilter(e.target.value)}
            className={`input-field w-full transition-all duration-200 ${
              isDarkMode ? 'dark' : 'light'
            }`}
          >
            <option value="all">All Needed Skills ({allProjectSkills.length})</option>
            {allProjectSkills.map(skill => (
              <option key={skill} value={skill}>{skill}</option>
            ))}
          </select>
        </div>

        <div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className={`input-field w-full transition-all duration-200 ${
              isDarkMode ? 'dark' : 'light'
            }`}
          >
            <option value="all">All Statuses</option>
            <option value="Planning">Planning</option>
            <option value="In Progress">In Progress</option>
            <option value="On Hold">On Hold</option>
            <option value="Completed">Completed</option>
          </select>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProjects.map((project) => {
          const isOwner = (project.creatorEmail && project.creatorEmail.toLowerCase() === currentUser?.email?.toLowerCase()) || 
                          (project.creator && project.creator.toLowerCase() === currentUser?.name?.toLowerCase());
          const isMember = isProjectOwnerOrMember(project, currentUser);
          const application = getUserApplication(project.id);
          const matchInfo = calculateProjectSkillMatch(project.requiredSkills, currentUser?.skills);
          const currentCount = project.currentTeam !== undefined ? project.currentTeam : (project.teamMembers?.length || 1);
          const totalSpots = project.teamSize || 3;
          const openSpots = Math.max(0, totalSpots - currentCount);

          return (
            <div 
              key={project.id} 
              className={`card flex flex-col justify-between transition-all duration-200 hover:shadow-xl ${
                isDarkMode ? 'dark' : 'light'
              } ${isOwner ? 'border-purple-500/30' : application ? 'border-amber-500/30' : ''}`}
            >
              <div>
                {/* Project Header & Relationship Badge */}
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-lg font-bold line-clamp-1">{project.name}</h3>
                    </div>
                    <div className="flex items-center space-x-2 text-xs text-dark-300">
                      <span>Led by {project.creator || 'Admin'}</span>
                      <span>•</span>
                      <span className={`px-2 py-0.5 rounded-full font-medium ${getStatusColor(project.status)}`}>
                        {project.status}
                      </span>
                    </div>
                  </div>

                  {/* Badges for current user */}
                  <div>
                    {isOwner ? (
                      <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-purple-500/20 text-purple-400 border border-purple-500/30 whitespace-nowrap">
                        Lead
                      </span>
                    ) : isMember ? (
                      <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-500/20 text-blue-400 border border-blue-500/30 whitespace-nowrap">
                        Member
                      </span>
                    ) : application ? (
                      <span className={`px-2 py-0.5 rounded text-[11px] font-bold whitespace-nowrap border ${
                        application.status === 'Accepted'
                          ? 'bg-green-500/20 text-green-400 border-green-500/30'
                          : application.status === 'Rejected'
                            ? 'bg-red-500/20 text-red-400 border-red-500/30'
                            : 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                      }`}>
                        {application.status === 'Accepted' ? 'Accepted' : 'Applied'}
                      </span>
                    ) : openSpots > 0 ? (
                      <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 whitespace-nowrap">
                        {openSpots} spot{openSpots > 1 ? 's' : ''} left
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-dark-700 text-dark-300 whitespace-nowrap">
                        Team Full
                      </span>
                    )}
                  </div>
                </div>

                {/* Description */}
                <p className={`text-sm line-clamp-2 mb-4 leading-relaxed ${
                  isDarkMode ? 'text-dark-300' : 'text-light-600'
                }`}>
                  {project.description || 'No description provided.'}
                </p>

                {/* Skill Match Indicator */}
                {currentUser && (
                  <div className={`p-2.5 rounded-lg mb-4 text-xs border ${
                    matchInfo.matchPercentage >= 75
                      ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-300'
                      : matchInfo.matchPercentage >= 40
                        ? 'bg-blue-500/10 border-blue-500/20 text-blue-300'
                        : 'bg-dark-800/60 border-dark-700 text-dark-300'
                  }`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold flex items-center">
                        <Target className="h-3.5 w-3.5 mr-1" />
                        Skill Fit Match:
                      </span>
                      <span className="font-bold">{matchInfo.matchPercentage}% ({matchInfo.matchCount}/{matchInfo.totalRequired} skills)</span>
                    </div>
                    <div className="w-full bg-black/30 rounded-full h-1.5 overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${
                          matchInfo.matchPercentage >= 75 ? 'bg-emerald-400' : matchInfo.matchPercentage >= 40 ? 'bg-blue-400' : 'bg-gray-400'
                        }`}
                        style={{ width: `${matchInfo.matchPercentage}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Needed Skills Breakdown */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-semibold uppercase tracking-wider ${
                      isDarkMode ? 'text-dark-400' : 'text-light-500'
                    }`}>
                      Needed Skills
                    </span>
                    <span className="text-[11px] text-gray-500">
                      {project.requiredSkills?.length || 0} required
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {(project.requiredSkills || []).map((skill) => {
                      const skillName = typeof skill === 'string' ? skill : skill?.name || '';
                      const isMatched = matchInfo.matchedSkills.some(s => s.toLowerCase() === skillName.toLowerCase());

                      return (
                        <span
                          key={skillName}
                          className={`px-2 py-0.5 rounded text-xs flex items-center space-x-1 transition-colors ${
                            isMatched
                              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-medium'
                              : isDarkMode
                                ? 'bg-dark-800 text-dark-300 border border-dark-700'
                                : 'bg-light-100 text-light-700 border border-light-200'
                          }`}
                          title={isMatched ? `You possess this skill in your profile!` : `Needed skill`}
                        >
                          {isMatched && <CheckCircle className="h-3 w-3 text-emerald-400 mr-0.5" />}
                          <span>{skillName}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="space-y-1.5 mb-4">
                  <div className="flex items-center justify-between text-xs">
                    <span className={isDarkMode ? 'text-dark-400' : 'text-light-500'}>Progress</span>
                    <span className="font-semibold">{project.progress || 0}%</span>
                  </div>
                  <div className="w-full bg-dark-800 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-300 ${getProgressColor(project.progress || 0)}`}
                      style={{ width: `${project.progress || 0}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Project Card Footer */}
              <div className={`pt-3 border-t flex flex-col space-y-3 ${
                isDarkMode ? 'border-dark-800' : 'border-light-200'
              }`}>
                <div className="flex items-center justify-between text-xs text-dark-400">
                  <div className="flex items-center space-x-1.5">
                    <Users className="h-3.5 w-3.5" />
                    <span>Team: <strong className="text-white">{currentCount}/{totalSpots}</strong></span>
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>{project.endDate ? new Date(project.endDate).toLocaleDateString() : 'Ongoing'}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-1">
                  <div className="flex items-center space-x-2">
                    {isAdmin && (
                      <button
                        onClick={() => handleDeleteProject(project.id, project.name)}
                        className="p-1.5 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                        title="Delete project from Neo4j (Admin)"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                    {(isOwner || isAdmin) && (
                      <Link
                        to={`/projects/${project.id}`}
                        className="px-2 py-0.5 rounded text-xs font-semibold bg-primary-500/10 text-primary-400 hover:bg-primary-500/20 flex items-center space-x-1"
                        title="Edit Project Details & Skills"
                      >
                        <Edit3 className="h-3 w-3" />
                        <span>Edit</span>
                      </Link>
                    )}
                    <Link
                      to={`/projects/${project.id}`}
                      className="text-primary-400 hover:text-primary-300 text-xs font-semibold"
                    >
                      View Details →
                    </Link>
                  </div>

                  {/* Apply action button */}
                  <div>
                    {isMember ? (
                      <span className="text-xs text-purple-400 font-medium flex items-center">
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                        On Team
                      </span>
                    ) : application ? (
                      <span className="text-xs text-amber-300 font-medium flex items-center">
                        <Clock className="h-3.5 w-3.5 mr-1" />
                        Application {application.status}
                      </span>
                    ) : (
                      <button
                        onClick={() => handleOpenApplyModal(project)}
                        className="btn-primary py-1.5 px-3 text-xs flex items-center space-x-1 shadow-sm"
                      >
                        <Send className="h-3 w-3" />
                        <span>Apply</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredProjects.length === 0 && (
        <div className={`text-center py-12 px-6 rounded-2xl border border-dashed transition-colors duration-200 ${
          isDarkMode ? 'border-dark-700 bg-dark-900/50 text-dark-300' : 'border-light-300 bg-light-100/50 text-light-600'
        }`}>
          <div className="max-w-md mx-auto space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-blue-500/10 flex items-center justify-center text-blue-400">
              <FolderGit2 className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-bold text-white">No Projects Found</h3>
            <p className="text-sm">
              {activeTab === 'applied'
                ? "You haven't submitted any applications yet. Explore the available projects above and apply to teams looking for your skills!"
                : activeTab === 'my'
                  ? "You haven't published or joined any projects yet. Create a new project or explore available projects to apply!"
                  : "No projects match your current search terms or skill filters. Try adjusting your search or clearing filters."}
            </p>
            <div className="flex justify-center space-x-3 pt-2">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterStatus('all');
                  setSelectedSkillFilter('all');
                  setActiveTab('all');
                }}
                className="btn-secondary text-sm"
              >
                Reset Filters
              </button>
              <Link to="/projects/new" className="btn-primary text-sm inline-flex items-center">
                <Plus className="h-4 w-4 mr-1.5" />
                Create New Project
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Apply to Project Modal */}
      {applyModalProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
          <div className={`card max-w-lg w-full max-h-[90vh] overflow-y-auto border shadow-2xl ${
            isDarkMode ? 'bg-dark-900 border-dark-700 text-white' : 'bg-white border-light-200 text-light-950'
          }`}>
            <div className="flex items-center justify-between pb-4 border-b border-dark-800">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-primary flex items-center justify-center text-white">
                  <Send className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Apply to Join Project</h3>
                  <p className="text-xs text-dark-300">Submit your application to the project owner</p>
                </div>
              </div>
              <button 
                onClick={handleCloseApplyModal}
                className="p-1 rounded-lg hover:bg-dark-800 text-dark-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmitApplication} className="space-y-4 py-4">
              {/* Project Overview */}
              <div className="p-3 rounded-xl bg-dark-800/60 border border-dark-700 space-y-1.5">
                <div className="text-base font-bold text-white">{applyModalProject.name}</div>
                <div className="text-xs text-dark-300 line-clamp-2">{applyModalProject.description}</div>
                <div className="text-xs text-primary-400 pt-1">
                  Project Lead: <strong>{applyModalProject.creator || 'Admin'}</strong> • Spots: {Math.max(0, (applyModalProject.teamSize || 3) - (applyModalProject.currentTeam || 1))} open
                </div>
              </div>

              {/* Skills Fit Analysis */}
              {(() => {
                const modalMatch = calculateProjectSkillMatch(applyModalProject.requiredSkills, currentUser?.skills);
                return (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-semibold text-dark-300">Your Skill Fit:</span>
                      <span className={`font-bold ${modalMatch.matchPercentage >= 60 ? 'text-emerald-400' : 'text-blue-400'}`}>
                        {modalMatch.matchPercentage}% Match ({modalMatch.matchCount} of {modalMatch.totalRequired} skills)
                      </span>
                    </div>

                    <div className="w-full bg-dark-800 rounded-full h-2 overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-300 ${
                          modalMatch.matchPercentage >= 60 ? 'bg-emerald-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${modalMatch.matchPercentage}%` }}
                      />
                    </div>

                    <div className="pt-1">
                      <div className="text-xs text-dark-400 mb-1.5">Required Skills Status:</div>
                      <div className="flex flex-wrap gap-1.5">
                        {(applyModalProject.requiredSkills || []).map(s => {
                          const sName = typeof s === 'string' ? s : s?.name;
                          const hasSkill = modalMatch.matchedSkills.some(ms => ms.toLowerCase() === sName?.toLowerCase());
                          return (
                            <span 
                              key={sName}
                              className={`px-2 py-0.5 rounded text-xs flex items-center space-x-1 ${
                                hasSkill 
                                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-medium'
                                  : 'bg-dark-800 text-dark-400 border border-dark-700'
                              }`}
                            >
                              {hasSkill ? <CheckCircle className="h-3 w-3 text-emerald-400" /> : <span className="text-[10px] text-gray-500">•</span>}
                              <span>{sName}</span>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Pitch Message */}
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-dark-200">
                  Note to Project Lead:
                </label>
                <textarea
                  rows={4}
                  required
                  value={applicationPitch}
                  onChange={(e) => setApplicationPitch(e.target.value)}
                  placeholder="Introduce yourself, mention your relevant experience, and explain how you can help this project succeed..."
                  className={`input-field w-full text-sm resize-none ${isDarkMode ? 'dark' : 'light'}`}
                />
                <p className="text-[11px] text-dark-400">
                  The project lead will receive your note and your public SkillMesh profile skills.
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-3 border-t border-dark-800">
                <button
                  type="button"
                  onClick={handleCloseApplyModal}
                  className="btn-secondary text-sm"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary text-sm flex items-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      <span>Confirm & Apply</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;
